import React, { useState, useEffect, useRef, useCallback } from 'react';
import { dettaglioAPI } from '../../services/api';
import { useSalvaCampo } from '../../utils/salvaCampo';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { momentoItaliano } from '../../utils/oraItaliana';
import { messaggioDa } from '../../utils/notifiche';

/**
 * IntegrazioniVisita — i testi che il medico della visita aggiunge sotto il
 * riquadro «Visita» (Alex, 24 settembre 2026; migration 236).
 *
 * Il riquadro si scrive fino alle 23:59 del giorno dopo la visita; dopo, le
 * correzioni si scrivono QUI, con data, ora e autore, e il testo originale
 * resta com'era. Chi puo' aggiungere e chi puo' modificare lo dice il server
 * (services/riquadroVisita.js): qui si disegna la sua risposta, e basta.
 */

const intestazione = (i) => {
    const m = momentoItaliano(i.created_at);
    return `Integrazione del ${m.data} alle ${m.ora}${i.medico_cognome ? ` · Dr. ${i.medico_cognome}` : ''}`;
};

/* Un'integrazione ancora modificabile: si salva da sola uscendo dal campo,
   come ogni altro testo (regola di Alex). */
const IntegrazioneAperta = ({ integrazione }) => {
    const [val, setVal] = useState(integrazione.testo || '');
    const salvatoUltimo = useRef(integrazione.testo || '');
    const [errore, setErrore] = useState(null);
    const [versione, setVersione] = useState(0);
    const dirty = useSporco(val, versione);

    const scrivi = useCallback(async (_c, testo) => {
        try {
            await dettaglioAPI.modificaIntegrazione(integrazione.id, testo);
            salvatoUltimo.current = testo;
            setErrore(null);
        } catch (e) {
            setErrore(messaggioDa(e, "Non riesco a salvare l'integrazione"));
            throw e;
        }
    }, [integrazione.id]);
    const { stato, salva, nonSalvati } = useSalvaCampo(scrivi, () => setVersione((x) => x + 1));
    useUscitaProtetta(dirty || nonSalvati.length > 0);
    const st = stato.testo;

    const esci = () => {
        if (val === salvatoUltimo.current) return;
        if (!val.trim()) { setErrore("Il testo dell'integrazione è obbligatorio"); return; }
        salva('testo', val);
    };

    return (
        <div className="pd-integrazione" data-salva-da-solo>
            <div className="pd-campo-testa">
                <span className="pd-integrazione-testa">{intestazione(integrazione)}</span>
                {st === 'in_corso' && <span className="pd-campo-stato">salvataggio…</span>}
                {st === 'salvato' && <span className="pd-campo-ok">✓ salvato</span>}
            </div>
            <textarea className={`pd-campo-ta ${st === 'non_salvato' || errore ? 'pd-campo-ta--rosso' : ''}`}
                      rows="3" value={val} onChange={(e) => setVal(e.target.value)} onBlur={esci} />
            {errore && <div className="pd-campo-errore">{errore}</div>}
        </div>
    );
};

const IntegrazioniVisita = ({ visitaId }) => {
    const [elenco, setElenco] = useState([]);
    const [puoAggiungere, setPuoAggiungere] = useState(false);
    const [nuova, setNuova] = useState(null);      // null = chiusa, stringa = testo
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [giro, setGiro] = useState(0);

    useEffect(() => {
        let alive = true;
        dettaglioAPI.integrazioni(visitaId)
            .then((r) => {
                if (!alive || !r.data.success) return;
                setElenco(r.data.data.integrazioni || []);
                setPuoAggiungere(!!r.data.data.puo_aggiungere);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [visitaId, giro]);

    /* Un testo scritto e non ancora aggiunto non si perde uscendo. */
    useUscitaProtetta(!!(nuova && nuova.trim()));

    const aggiungi = async () => {
        if (!nuova || !nuova.trim()) { setErrore("Il testo dell'integrazione è obbligatorio"); return; }
        setInCorso(true);
        try {
            await dettaglioAPI.nuovaIntegrazione(visitaId, nuova);
            setNuova(null);
            setErrore(null);
            setGiro((g) => g + 1);
        } catch (e) {
            setErrore(messaggioDa(e, "Non riesco a salvare l'integrazione"));
        } finally {
            setInCorso(false);
        }
    };

    if (!elenco.length && !puoAggiungere) return null;

    return (
        <div className="pd-integrazioni">
            {elenco.map((i) => (i.modificabile
                ? <IntegrazioneAperta key={i.id} integrazione={i} />
                : (
                    <div key={i.id} className="pd-integrazione">
                        <div className="pd-integrazione-testa">{intestazione(i)}</div>
                        <div className="pd-integrazione-testo">{i.testo}</div>
                    </div>
                )))}
            {puoAggiungere && nuova === null && (
                <button type="button" className="pd-campo-save" onClick={() => { setNuova(''); setErrore(null); }}>
                    Aggiungi integrazione
                </button>
            )}
            {puoAggiungere && nuova !== null && (
                <div className="pd-integrazione">
                    <div className="pd-integrazione-testa">Nuova integrazione</div>
                    <textarea className="pd-campo-ta" rows="3" value={nuova} autoFocus
                              placeholder="Che cosa aggiungi o correggi…"
                              onChange={(e) => setNuova(e.target.value)} />
                    {errore && <div className="pd-campo-errore">{errore}</div>}
                    <div className="pd-integrazione-azioni">
                        <button type="button" className="pd-campo-save" disabled={inCorso} onClick={aggiungi}>
                            {inCorso ? 'Salvataggio…' : 'Salva integrazione'}
                        </button>
                        <button type="button" className="pd-integrazione-annulla" disabled={inCorso}
                                onClick={() => { setNuova(null); setErrore(null); }}>
                            Annulla
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntegrazioniVisita;
